import React, {Component} from "react";
import {DataGridContainer} from "app/modules/common/grid/data-grid-container";
import {DataGridHeader} from "app/modules/common/grid/data-grid-header";
import {
    Box,
    Button,
    Grid,
    InputBase, Stack,
    Typography
} from "@mui/material";
import {DataGridBody} from "app/modules/common/grid/data-grid-body";
import {DataGridPro, GridRenderCellParams} from "@mui/x-data-grid-pro";
import {inject, provider, useInstance} from "react-ioc";
import {observer} from "mobx-react";
import {OrdersProgressStore} from "app/modules/company/components/orders/progress/OrdersStore";
import {NavigationStore} from "app/modules/company/stores/NavigationStore";
import {PageTitleChip} from "app/modules/common/layout/components/page-title";
import {BoxCenter} from "app/modules/common/layout/components/containers";
import {SearchPaper, SearchVerDivider} from "app/modules/common/layout/components/search";
import {DateTimeHelper} from "app/modules/common/helpers/DateTimeHelper";
import {
    OrderModelListView,
    OrderStopStatusType,
    OrderStopType,
    TenantClaims, VehicleListModel
} from "app/modules/company/api/clients.api";
import {Loading} from "app/modules/common/layout/components/loading";
import {MainStore} from "app/modules/company/stores/MainStore";
import {LoadingButton} from "@mui/lab";
import {StringHelper} from "app/modules/common/helpers/StringHelper";
import {
    SectionDatePickerField,
    SectionTextField
} from "app/modules/common/details/text-field";
import {DriverFilter} from "app/modules/company/components/filters/drivers";
import {VehicleFilter} from "app/modules/company/components/filters/vehicle";
import {LocationLink} from "app/modules/common/details/location-link";

import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import InventoryIcon from '@mui/icons-material/Inventory';
import {orderStatusColorMap} from "app/modules/company/components/orders/all";
import moment from "moment";
import {TrackingDetails} from "app/modules/company/components/vehicles/components/tracking-details";

const columns = [
    {
        field: 'status',
        headerName: '',
        maxWidth: 30,
        flex: 1,
        renderCell: (params: GridRenderCellParams<OrderModelListView>) => (
            <Box sx={{ width: "5px", height: "100%", backgroundColor: orderStatusColorMap.get(params.row.status) }} />
        ),
    },
    {
        field: 'id',
        headerName: 'ID',
        maxWidth: 100,
        flex: 1,
        renderCell: (params: GridRenderCellParams<OrderModelListView>) => {
            const store = useInstance(OrdersProgressStore);
            return <Button variant={"text"} onClick={() => store.openOrder(params.row.id)}>{params.row.id}</Button>;
        },
    },
    {
        field: 'from',
        headerName: 'From',
        minWidth: 200,
        valueGetter: (params: GridRenderCellParams<OrderModelListView>) =>
            `${params.row.from || 'N/A'}`,
    },
    {
        field: 'pickupdate',
        headerName: 'PickUp',
        width: 90,
        renderCell: (params: GridRenderCellParams<OrderModelListView>) => (
            <div>
                <div>{DateTimeHelper.formatMomentDateShort(params.row.pickUp)}</div>
                <b><div>{DateTimeHelper.formatMomentTimeShortUtcToLocal(params.row.pickUp)}</div></b>
            </div>
        ),
    },
    {
        field: 'to',
        headerName: 'To',
        minWidth: 200,
        valueGetter: (params: GridRenderCellParams<OrderModelListView>) =>
            `${params.row.to || 'N/A'}`,
    },
    {
        field: 'dropdate',
        headerName: 'Drop',
        width: 90,
        renderCell: (params: GridRenderCellParams<OrderModelListView>) => (
            <div>
                <div>{DateTimeHelper.formatMomentDateShort(params.row.drop)}</div>
                <b><div>{DateTimeHelper.formatMomentTimeShortUtcToLocal(params.row.drop)}</div></b>
            </div>
        ),
    },
    {
        field: 'locatedAt',
        headerName: 'LocatedAt',
        maxWidth: 150,
        flex: 1,
        renderCell: (params: GridRenderCellParams<VehicleListModel>) => {
            let hours = 0;
            if(!!params.row.locatedAt) {
                let duration = moment.duration(moment(new Date()).diff(params.row.locatedAt));
                hours = duration.asHours();
            }

            return <Stack direction={"column"}>
                <div>{DateTimeHelper.formatMomentDateTimeShortUtcToLocal(params.row.locatedAt)}</div>
                {params.row.locatedAt && <Box sx={{ color: Math.abs(hours) >= 2 ? "red" : undefined }}><b>{params.row.locatedAt?.fromNow()}</b></Box>}
            </Stack>;
        },
    },
    {
        field: 'location',
        headerName: 'Location',
        minWidth: 450,
        flex: 2,
        renderCell: (params: GridRenderCellParams<OrderModelListView>) => {
            return <div>
                <Stack direction={"row"} justifyContent="center" alignItems="center" spacing={1}>
                    {params.row.stopStatusType == OrderStopStatusType.Heading && <LocalShippingIcon fontSize={"small"}/>}
                    {params.row.stopStatusType == OrderStopStatusType.Heading && params.row.remainingMiles > 0 && <Typography variant="subtitle2" component={"span"} sx={{ fontSize: "12px" }}>{" "}{params.row.remainingMiles} mi</Typography>}
                    {params.row.stopStatusType == OrderStopStatusType.Heading && <ArrowForwardIcon fontSize={"small"}/>}
                    {params.row.stopStatusType == OrderStopStatusType.Arrived && <ScheduleIcon fontSize={"small"}/> }
                    {params.row.stopStatusType != null && <Typography variant="subtitle2" component={"span"}>{params.row.stopStatusType == OrderStopStatusType.Arrived && <span>at</span>}</Typography>}
                    {params.row.stopStatusType != null && (params.row.stopType == OrderStopType.Shipper ? <InventoryIcon fontSize={"small"}/> : <SportsScoreIcon fontSize={"small"}/>)}
                    {params.row.stopStatusType != null && <Typography variant="subtitle2" component={"span"}>{params.row.stopState} ({params.row.currentStop}/{params.row.stops})</Typography>}
                    {params.row.location && <Button variant={"text"} sx={{height: "10px"}} onClick={() => navigator.clipboard.writeText(params.row.location)}>COPY</Button>}
                    <LocationLink location={params.row.location} locationDisplay={params.row.displayLocation}/>
                </Stack>
            </div>;
        },
    },
    {
        field: 'vehicle',
        headerName: 'Vehicle',
        minWidth: 250,
        flex: 2,
        renderCell: (params: GridRenderCellParams<OrderModelListView>) => (
            <div>
                <Stack direction={"row"} spacing={1} alignItems={"center"}>
                    <TrackingDetails vehicleId={params.row.vehicleId}><b>{params.row.vehicle}</b></TrackingDetails> {params.row.carrier?.length > 0 && <Typography variant="caption" display="block"> - {params.row.carrier}</Typography>}
                </Stack>
                <div>
                    {params.row.drivers?.join(", ")}
                </div>
                {!params.row.drivers?.length && !params.row.vehicle && "N/A"}
            </div>
        ),
    },
    {
        field: 'miles',
        headerName: 'Miles',
        maxWidth: 90,
        type: 'number',
        flex: 1
    },
    {
        field: 'customer',
        headerName: 'Customer',
        minWidth: 300,
        flex: 2,
        renderCell: (params: GridRenderCellParams<OrderModelListView>) => (
            <div>
                <div>{params.row.customer || 'N/A'}</div>
                {params.row.contact?.length > 0 && <Typography variant="caption" display="block">
                    {params.row.contact}
                </Typography>}
            </div>
        ),
    },
    {
        field: 'po',
        headerName: 'PO',
        width: 100,
        valueGetter: (params: GridRenderCellParams<OrderModelListView>) =>
            `${StringHelper.formatNull(params.row.orderNumber)}`,
    },
    {
        field: 'weight',
        headerName: 'Weight',
        width: 110,
        renderCell: (params: GridRenderCellParams<OrderModelListView>) => (
            <div>
                <div><b>{params.row.weight}</b> lb</div>
                <div><b>{params.row.pieceUnits}</b> pc</div>
            </div>
        ),
    }
];

@provider(OrdersProgressStore)
@observer
export default class Index extends Component<any, any> {
    @inject ordersStore: OrdersProgressStore;
    @inject navigationStore: NavigationStore;
    @inject mainStore: MainStore;

    render() {
        if(this.ordersStore.isInitLoading) {
            return <Loading />;
        }

        return <DataGridContainer>
            <Grid item xs={this.mainStore.checkClaim(TenantClaims.OrderEdit) ? 8 : 12}>
                <PageTitleChip Title={this.navigationStore.title} TotalRows={this.ordersStore.totalRows}/>
            </Grid>
            {this.mainStore.checkClaim(TenantClaims.OrderNew) && <Grid item xs={4} textAlign={"right"}>
                <LoadingButton loading={this.ordersStore.isLoading} variant={"contained"} onClick={this.ordersStore.newBlankOrder}>New Blank Order</LoadingButton>
            </Grid>}
            <DataGridHeader>
                <BoxCenter>
                    <Stack direction={"row"}>
                        <SearchPaper width={400}>
                            <InputBase
                                sx={{ ml: 1, flex: 1 }}
                                value={this.ordersStore.filters.$.search.value}
                                onChange={(e)=>this.ordersStore.filters.$.search.onChange(e.target.value)}
                                placeholder="Search"
                                inputProps={{ 'aria-label': 'search' }}
                            />
                            <SearchVerDivider />
                            <Button variant="text" onClick={this.ordersStore.applyFilters}>Search</Button>
                        </SearchPaper>
                        <Button variant={"text"} size={"small"} sx={{ ml: 2 }} onClick={this.ordersStore.showHideAdvanceStore}>Advance Filters</Button>
                    </Stack>
                </BoxCenter>
            </DataGridHeader>
            {this.ordersStore.isAdvanceFilterOpen && <Box sx={{ mb: 1, width: "100%", backgroundColor: "#fcfcfc", p: 2 }}>
                <Stack direction={"row"} spacing={2} sx={{ width: "100%" }}>
                    <SectionDatePickerField
                        field={this.ordersStore.filters.$.from}
                        label="From"
                        width={"150px"}
                    />
                    <SectionDatePickerField
                        field={this.ordersStore.filters.$.to}
                        label="To"
                        width={"150px"}
                    />
                    <SectionTextField
                        label={"From Zip"}
                        field={this.ordersStore.filters.$.fromZip}
                        type={"number"}
                        width={"100px"}
                    />
                    <SectionTextField
                        label={"From Radius"}
                        field={this.ordersStore.filters.$.fromRadius}
                        type={"number"}
                        width={"100px"}
                    />
                    <SectionTextField
                        label={"To Zip"}
                        field={this.ordersStore.filters.$.toZip}
                        type={"number"}
                        width={"100px"}
                    />
                    <SectionTextField
                        label={"To Radius"}
                        field={this.ordersStore.filters.$.toRadius}
                        type={"number"}
                        width={"100px"}
                    />
                    <DriverFilter value={this.ordersStore.filters.$.driverIds} width={"20%"} />
                    <VehicleFilter value={this.ordersStore.filters.$.vehicleIds} width={"20%"} />
                </Stack>
            </Box>}
            <DataGridBody openFilter={this.ordersStore.isAdvanceFilterOpen}>
                <DataGridPro
                    disableVirtualization
                    rows={this.ordersStore.items}
                    columns={columns}
                    pageSize={this.ordersStore.pageSize}
                    rowsPerPageOptions={this.ordersStore.pageSizeOptions}
                    rowCount={this.ordersStore.totalRows}
                    page={this.ordersStore.skipRows/this.ordersStore.pageSize}
                    loading={this.ordersStore.isLoading}
                    pagination
                    paginationMode="server"
                    disableColumnFilter
                    disableColumnSelector
                    disableSelectionOnClick
                    checkboxSelection={false}
                    onPageChange={(page) => this.ordersStore.setPage(page)}
                    onPageSizeChange={(pageSize) => this.ordersStore.setPageSize(pageSize) }/>
            </DataGridBody>
        </DataGridContainer>;
    }

    componentDidMount() {
        this.ordersStore.init();
    }
}
